import { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  CreditTransactionDetailModal,
  PortalCreditTransactionsLedger,
  UsdWalletTransactionDetailModal,
  downloadBlob,
  resolveCreditPeriodDateRange,
  usePortalLedgerDetailState,
  validateCreditExportPeriod,
  type CreditLedgerFilterState,
  type CreditLedgerListResponse,
  type CreditLedgerSortField,
} from '@kioskgaming/ui';
import AppLayout from '../components/layout/AppLayout';
import { getTableSortColumns } from '../config/tableSortConfig';
import useTableSort from '../hooks/useTableSort';
import {
  exportAgentWalletTransactions,
  fetchAgentCreditTransactionById,
  fetchAgentTransactions,
  fetchAgentUsdLedgerEntryById,
} from '../services/api';

export default function AgentTransactionsPage() {
  const [data, setData] = useState<CreditLedgerListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [sortFetchTick, setSortFetchTick] = useState(0);

  const tableSort = useTableSort({
    columns: getTableSortColumns('agentCreditTransactions'),
    onChange: () => setSortFetchTick((t) => t + 1),
  });

  const hostSort = useMemo(() => {
    const sort = (data?.pagination?.sort ?? tableSort.sortBy ?? 'created') as CreditLedgerSortField;
    const sortDirRaw = data?.pagination?.sortDir ?? tableSort.sortOrder;
    const sortDir = String(sortDirRaw ?? 'desc').toLowerCase() === 'asc' ? 'asc' : 'desc';
    return { sort, sortDir: sortDir as 'asc' | 'desc' };
  }, [tableSort.sortBy, tableSort.sortOrder, data?.pagination?.sort, data?.pagination?.sortDir]);

  const ledgerDetail = usePortalLedgerDetailState({
    creditRows: data?.items ?? [],
    fetchCreditById: async (id) => {
      const res = await fetchAgentCreditTransactionById(id);
      if (!res.success || !res.data?.item) throw new Error(res.message || 'Not found');
      return res.data.item;
    },
    fetchUsdById: async (id) => {
      const res = await fetchAgentUsdLedgerEntryById(id);
      if (!res.success || !res.data?.item) throw new Error(res.message || 'Not found');
      return res.data.item;
    },
    onUnavailable: () =>
      toast.error('Related transaction is unavailable or outside your permission.'),
  });

  const onFetch = useCallback(
    async ({
      filters,
      page,
      limit,
    }: {
      filters: CreditLedgerFilterState;
      page: number;
      limit: number;
    }) => {
      setLoading(true);
      try {
        const res = await fetchAgentTransactions({
          page,
          limit,
          owner_scope: 'self',
          period: filters.period,
          ...(filters.period === 'custom'
            ? { date_from: filters.dateFrom, date_to: filters.dateTo }
            : {}),
          type: filters.typeFilter,
          search: filters.search.trim() || undefined,
          sort: tableSort.sortBy ?? 'created',
          sort_dir: (tableSort.sortOrder ?? 'DESC').toLowerCase(),
          ...(tableSort.sortBy ? { sortBy: tableSort.sortBy } : {}),
          ...(tableSort.sortOrder ? { sortOrder: tableSort.sortOrder } : {}),
        });
        if (res.success && res.data) {
          setData({
            items: res.data.items,
            pagination: res.data.pagination,
            summary: res.data.summary,
          });
        } else {
          setData(null);
          toast.error(res.message || 'Could not load transactions');
        }
      } catch {
        setData(null);
        toast.error('Could not load transactions');
      } finally {
        setLoading(false);
      }
    },
    [tableSort.sortBy, tableSort.sortOrder],
  );

  const handleExport = async ({ filters }: { filters: CreditLedgerFilterState }) => {
    const dates = resolveCreditPeriodDateRange(filters.period, filters.dateFrom, filters.dateTo);
    const periodErr = validateCreditExportPeriod(dates.dateFrom, dates.dateTo);
    if (periodErr) {
      toast.error(periodErr);
      return;
    }
    setExporting(true);
    try {
      const { blob, filename } = await exportAgentWalletTransactions({
        owner_scope: 'self',
        period: filters.period,
        ...(filters.period === 'custom'
          ? { date_from: dates.dateFrom, date_to: dates.dateTo }
          : {}),
        type: filters.typeFilter,
        search: filters.search.trim() || undefined,
      });
      downloadBlob(blob, filename);
      toast.success('Credit transactions exported');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <AppLayout>
      <PortalCreditTransactionsLedger
        title="Credit wallet transactions"
        loading={loading}
        data={data}
        onFetch={onFetch}
        onExport={handleExport}
        exporting={exporting}
        onViewDetail={(row) => ledgerDetail.openCredit(row)}
        description="Review credit wallet activity with filters, sorting, and detailed drill-down."
        hostSort={{
          sort: hostSort.sort,
          sortDir: hostSort.sortDir,
          onSort: (field) => tableSort.toggleSort(field),
        }}
        sortFetchTick={sortFetchTick}
      />

      <CreditTransactionDetailModal
        entry={ledgerDetail.creditDetail}
        onClose={ledgerDetail.closeCredit}
        accountLabel="Your agent account"
        onOpenUsdSource={ledgerDetail.handleOpenUsdSource}
        relatedLoading={ledgerDetail.relatedLoading}
        resolveUsdConversion={ledgerDetail.resolveUsdConversion}
      />
      <UsdWalletTransactionDetailModal
        row={ledgerDetail.usdDetail}
        onClose={ledgerDetail.closeUsd}
        onOpenCreditSource={ledgerDetail.handleOpenCreditSource}
        onOpenUsdSource={ledgerDetail.handleOpenUsdSource}
        relatedLoading={ledgerDetail.relatedLoading}
      />
    </AppLayout>
  );
}
